/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * "Show times in": the gym's clock or this device's. The web never saves a zone;
 * choosing gym time deletes one a phone saved.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  RadioGroup,
} from "@lemonadetraining/web-ui";
import { deleteField, doc, serverTimestamp, setDoc } from "firebase/firestore";
import {
  COLLECTIONS,
  firebaseDB,
  NOTIFICATION_TYPE,
  notifyActions,
  useAppDispatch,
  useAppSelector,
  useAppStore,
  userActions,
} from "@lemonadetraining/web-shared";
import { isTimeZoneDisplayPreference } from "@lemonadetraining/contracts";
import { resolveWallClock } from "../../lib/gymClock";
import { useOwnGymZone } from "../../hooks/useOwnGymZone";
import { useGymZone } from "../../hooks/useGymZone";
import { useGymTodayKey } from "../../hooks/useGymTodayKey";
import { useTimeFormat } from "../../hooks/useTimeFormat";
import { useUserAccess } from "../../hooks/useUserAccess";
import OptionRow from "./OptionRow";
import SavedIndicator from "./SavedIndicator";

export const SHOW_TIMES_IN_ID = "show-times-in";

const SAMPLE_TIME = "18:00";

const ShowTimesInCard: React.FC = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const uid = useAppSelector((state) => state.user.user?.uid);
  const mode = useAppSelector((state) => state.user.timeZoneDisplay);
  const synced = useAppSelector((state) => state.user.preferencesSynced);
  const own = useOwnGymZone();
  const gym = useGymZone(own.zone);
  const todayKey = useGymTodayKey(gym.gymZone);
  const { formatTime } = useTimeFormat();
  const { isBoxStaff, isOwner } = useUserAccess();
  const { hash } = useLocation();
  const [saved, setSaved] = useState(false);
  const seqRef = useRef(0);
  const arrivedRef = useRef(false);

  const failed = own.error !== null;
  const ready = failed || (!gym.isLoading && gym.hasGymZone);
  const city = gym.city;

  const examples = useMemo(() => {
    if (!gym.gymZone) return null;
    const at = resolveWallClock(todayKey, SAMPLE_TIME, gym.gymZone);
    // 18:00 skipped by a clock change today: show no example rather than a guess.
    if (at.status !== "ok") return null;
    return {
      gym: formatTime(at.instantMs, gym.gymZone),
      device: formatTime(at.instantMs, gym.browserZone),
    };
  }, [todayKey, gym.gymZone, gym.browserZone, formatTime]);

  const handleChange = useCallback(
    async (next: string) => {
      if (failed || !uid || !isTimeZoneDisplayPreference(next) || next === mode) return;
      const previous = mode;

      const seq = (seqRef.current += 1);
      setSaved(false);
      dispatch(userActions.setTimeZoneDisplay(next));

      // Only a phone saves a zone. Gym time removes it, so a zone never outlives
      // the choice that needed it.
      const payload = {
        timeZoneDisplay: next,
        updatedAt: serverTimestamp(),
        updatedFrom: "web",
        ...(next === "gym" ? { deviceTimeZone: deleteField() } : {}),
      };

      try {
        await setDoc(
          doc(firebaseDB, COLLECTIONS.Users, uid, COLLECTIONS.UserSettings, "preferences"),
          payload,
          { merge: true }
        );
        if (seqRef.current !== seq) return;
        setSaved(true);
      } catch {
        // A rollback after a sign-out would land in the next user's store.
        if (seqRef.current !== seq || store.getState().user.user?.uid !== uid) return;
        dispatch(userActions.setTimeZoneDisplay(previous));
        dispatch(
          notifyActions.sendNotification({
            severity: NOTIFICATION_TYPE.error,
            title: "",
            message: "Couldn't save your choice. Please try again.",
          })
        );
      }
    },
    [failed, uid, mode, dispatch, store]
  );

  const hideSaved = useCallback(() => setSaved(false), []);

  // Waits for the saved choice so focus lands on the option that stays checked.
  // The primitives here do not forward refs, so the target is found in the DOM.
  useEffect(() => {
    if (arrivedRef.current || !ready || !synced || hash !== `#${SHOW_TIMES_IN_ID}`) return;
    arrivedRef.current = true;
    const root = document.getElementById(SHOW_TIMES_IN_ID);
    const target = root?.querySelector<HTMLElement>(
      failed ? '[role="alert"]' : '[role="radio"][data-state="checked"]'
    );
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    root?.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
    // focusVisible: a focus that follows a click hides its ring by default.
    const focusOptions: FocusOptions & { focusVisible?: boolean } = {
      preventScroll: true,
      focusVisible: true,
    };
    target?.focus(focusOptions);
  }, [ready, synced, failed, hash]);

  if (!ready) return null;

  const optionSentence =
    mode === "device"
      ? "Times, today's workout and reminders follow your device's clock, so they change when you travel."
      : `Times follow your gym's clock${city ? ` (${city})` : ""}, wherever you are. Best if you train at your gym.`;

  return (
    <Card id={SHOW_TIMES_IN_ID} className="scroll-mt-4">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle id="appearance-zone-title">Show times in</CardTitle>
          <SavedIndicator visible={saved} onHidden={hideSaved} />
        </div>
        <CardDescription id="appearance-zone-note">
          How class times and today&apos;s workout are shown, here and in the app. Your reminders
          follow it too.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {failed ? (
          <Alert tabIndex={-1} data-testid="show-times-in-error">
            <AlertDescription>
              We couldn&apos;t load your gym&apos;s time zone. Refresh the page to try again.
            </AlertDescription>
          </Alert>
        ) : null}
        {examples ? (
          <p id="appearance-zone-caption" className="text-sm text-muted-foreground">
            A class at {formatTime(SAMPLE_TIME, null)} {city ?? "gym"} time shows as:
          </p>
        ) : null}
        <RadioGroup
          value={mode}
          onValueChange={handleChange}
          disabled={failed}
          aria-labelledby="appearance-zone-title"
          aria-describedby={[
            "appearance-zone-note",
            examples ? "appearance-zone-caption" : null,
            "appearance-zone-footer",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <OptionRow
            id="time-zone-gym"
            value="gym"
            label={city ? `Gym time (${city})` : "Gym time"}
            example={examples?.gym}
          />
          <OptionRow
            id="time-zone-device"
            value="device"
            label="My device's time"
            example={examples?.device}
          />
        </RadioGroup>
        <div
          id="appearance-zone-footer"
          className="flex flex-col gap-1 text-sm text-muted-foreground"
        >
          <p>{optionSentence}</p>
          <p>
            Choosing My phone&apos;s time saves your phone&apos;s time zone to your account.
            Choosing Gym time removes it.
          </p>
          {isBoxStaff || isOwner ? <p>Creating and editing classes always uses gym time.</p> : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default ShowTimesInCard;
